import React, { useState } from 'react';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import { SecondaryButton } from 'src/shared/components/button/Button';
import { UserType } from 'src/shared/types';
import useAuthService from 'src/shared/hooks/useAuthService';
import styles from './ChangeUserType.module.scss';
import UserTypeTag from '../user-type-tag/UserTypeTag';
import { useSetUserTypeMutation } from '../../state/api/userManagementGraphSlice';

type ChangeUserTypeProps = {
    email: string;
    userType: UserType;
    onChanged?: (userType: UserType) => void;
};

const targetTypeLabel: Record<UserType, string> = {
    [UserType.INTERNAL]: 'Internal',
    [UserType.EXTERNAL]: 'Customer',
};

/**
 * Super-admin control for correcting a user's internal/external type — most often
 * a customer contact invited as an internal user by mistake. The type drives seat
 * counts, licence validation on role assignment and upload attribution, so this is
 * deliberately a confirmed, single-purpose action rather than part of the profile save.
 *
 * Uses ControlledConfirmDialog rather than the uncontrolled `Confirm`: this renders
 * inside the edit-user `Modal`, and only the controlled dialog stacks above app modals.
 */
const ChangeUserType = ({ email, userType, onChanged }: ChangeUserTypeProps) => {
    const { isCurrentUserSuperAdmin } = useAuthService();
    const [setUserType, { isLoading }] = useSetUserTypeMutation();
    const [errorMessage, setErrorMessage] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    if (!isCurrentUserSuperAdmin) {
        return (
            <div className={styles.changeUserType}>
                <span className={styles.fieldLabel}>User Type</span>
                <UserTypeTag userType={userType} />
            </div>
        );
    }

    const targetType = userType === UserType.INTERNAL ? UserType.EXTERNAL : UserType.INTERNAL;
    const actionLabel = `Change to ${targetTypeLabel[targetType]}`;

    const onConfirm = async () => {
        setErrorMessage('');
        try {
            await setUserType({ email, userType: targetType }).unwrap();
            onChanged?.(targetType);
            setIsConfirmOpen(false);
        } catch {
            setErrorMessage('We could not change this user’s type. Please try again.');
            setIsConfirmOpen(false);
        }
    };

    return (
        <div className={styles.changeUserType}>
            <span className={styles.fieldLabel}>User Type</span>
            <div className={styles.current}>
                <UserTypeTag userType={userType} />
                <SecondaryButton
                    data-testid='change-user-type-trigger'
                    disabled={isLoading}
                    onClick={() => setIsConfirmOpen(true)}
                    className={styles.changeButton}
                >
                    {actionLabel}
                </SecondaryButton>
            </div>
            {errorMessage && (
                <div className={styles.error} data-testid='change-user-type-error'>
                    {errorMessage}
                </div>
            )}
            <ControlledConfirmDialog
                open={isConfirmOpen}
                onOpenChange={setIsConfirmOpen}
                variant='confirm'
                title={actionLabel}
                description={
                    <>
                        {email} will become a {targetTypeLabel[targetType].toLowerCase()} user. This changes how they are counted against
                        organisation seats and which roles they can hold. They will need to sign out and sign in again before it takes
                        effect for them.
                    </>
                }
                confirmLabel={actionLabel}
                onConfirm={onConfirm}
                isConfirming={isLoading}
            />
        </div>
    );
};

export default ChangeUserType;
